import type { ReactNode } from 'react';
import { useState, useCallback, useMemo, useRef } from 'react';
import { ErrorContext } from './ErrorContext';
import type { ErrorState } from '@/core/types/error-types';

export function ErrorProvider({ children }: { children: ReactNode }) {
  const [errors, setErrors] = useState<ErrorState[]>([]);
  const [currentError, setCurrentError] = useState<ErrorState | null>(null);
  // Use ref to track current error without causing re-renders
  const currentErrorRef = useRef<ErrorState | null>(null);

  const showError = useCallback((error: ErrorState) => {
    const errorWithId: ErrorState = {
      ...error,
      id: error.id || `error-${Date.now()}-${Math.random()}`,
      timestamp: new Date(),
    };

    setErrors((prev) => [...prev, errorWithId]);
    setCurrentError(errorWithId);
    currentErrorRef.current = errorWithId;
  }, []);

  const clearError = useCallback((id: string) => {
    setErrors((prev) => {
      const filtered = prev.filter((e) => e.id !== id);
      // If the current error is being removed, show the next one
      if (currentErrorRef.current?.id === id) {
        const nextError = filtered[filtered.length - 1] || null;
        setCurrentError(nextError);
        currentErrorRef.current = nextError;
      }
      return filtered;
    });
  }, []);

  const clearAllErrors = useCallback(() => {
    setErrors([]);
    setCurrentError(null);
    currentErrorRef.current = null;
  }, []);

  const value = useMemo(
    () => ({ errors, showError, clearError, clearAllErrors, currentError }),
    [errors, showError, clearError, clearAllErrors, currentError],
  );

  return <ErrorContext.Provider value={value}>{children}</ErrorContext.Provider>;
}
